import React, {useEffect, useMemo, useState} from 'react';
import moment from 'moment/moment';
import PropTypes from 'prop-types';
import {Collapse} from 'react-collapse';
import {useTranslation} from 'react-i18next';
import {connect} from 'react-redux';
import {Button, Divider, Form, Input, Select} from 'antd';
import {InfoCircleOutlined} from '@ant-design/icons';
import {
  cardTypesConstants,
  subscriptionFormFields,
  subscriptionFormValues,
  subscriptionPaymentTypesConstants,
  subscriptionStatusesConstants,
} from '../../../../constants';
import {StyledAddNewCardFormModal} from './StyledAddNewCardFormModal';
import {StyledBudgetDetailsFormSpace} from '../../SubscriptionPage/tabComponents/Overview/BudgetDetails/StyledBudgetDetails';
import CompanyTagSelect from '../../TransactionsPage/CompanyTagSelect/CompanyTagSelect';
import HardCardLimitSwitch from '../../SubscriptionsPage/HardCardLimitSwitch';
import IncreaseLimitAlert from '../../SubscriptionsPage/IncreaseLimitAlert';
import SubscriptionSelect from '../../SubscriptionsPage/SubscriptionSelect';
import Accordion from '../../../Accordion';
import EuroInput from '../../../EuroInput';
import SpinSmall from '../../../SpinSmall';
import CardBanner from '../CardBanner';
import {
  cardsHelpers,
  dateHelpers,
  BillOptions,
  CardLimitPeriodTypeOptions,
  formHelpers,
  StatusOptions,
  SubscriptionsCategoryOptions,
  subscriptionsHelpers, objectHelpers
} from '../../../../utils/helpers';
import {serviceStatsActions} from '../../../../state/actions/serviceStats.actions';

const {Item} = Form;

const {DIGITAL, SUBSCRIPTION} = cardTypesConstants;

const defaultCardType = DIGITAL;

const {
  billedFieldName,
  budgetLimitFieldName,
  cardLimitFieldName,
  cardLimitPeriodFieldName,
  limitPeriodShortFieldName,
  statusFieldName,
} = subscriptionFormFields;
const {
  defaultBilled,
  defaultBudgetLimitSubscription,
  defaultCardLimitPeriod
} = subscriptionFormValues;

const defaultFormValues = {
  [billedFieldName]: defaultBilled,
  card_type: defaultCardType,
  [budgetLimitFieldName]: defaultBudgetLimitSubscription,
  [cardLimitFieldName]: defaultBudgetLimitSubscription,
  [cardLimitPeriodFieldName]: defaultCardLimitPeriod,
  [statusFieldName]: subscriptionStatusesConstants.ACTIVE,
  category: undefined,
  cost_centers: [],
  name: '',
  owner: null,
  reason: '',
  service: ''
};

const minServiceSearchSymbolsLength = 3;

const getLimit = (cardType, paymentCardLimits) => {
  const {personalCardLimit, subscriptionCardLimit} = paymentCardLimits;
  return cardType === SUBSCRIPTION ? subscriptionCardLimit : personalCardLimit;
}

const {getCardLimitTypeFromBilled} = cardsHelpers;

const AddNewCardFormModal = ({
  dispatch,
  defaultCardType,
  getSuggestServices,
  loading = false,
  employees,
  employeeEmail,
  paymentCardLimits,
  onCancel,
  onOk,
  onSearch,
  open,
  ...rest
}) => {
  const [t] = useTranslation(['main', 'cards', 'subscriptions']);
  const [billOptions, ] = useState(BillOptions());
  const [cardLimitPeriodTypeOptions, ] = useState(CardLimitPeriodTypeOptions());
  const [cardType, setCardType] = useState(defaultCardType);
  const [initialFormValues, setInitialFormValues] = useState(defaultFormValues);
  const [categoryOptions, ] = useState(SubscriptionsCategoryOptions());
  const [statusOptions, ] = useState(StatusOptions());
  const [services, setServices] = useState({data: [], loading: false});
  const [isOpenDetails, setIsOpenDetails] = useState({subscriptions: false, user: true});
  const [isSelectedHardLimit, setIsSelectedHardLimit] = useState(false);

  const isPersonalCardType = useMemo(() => cardType !== SUBSCRIPTION, [cardType]);

  const maxLimit = useMemo(() => getLimit(cardType, paymentCardLimits), [cardType, paymentCardLimits]);

  const [form] = Form.useForm();

  const requiredRules = [{required: true, message: t('validation.fieldIsRequired')}]

  const budgetLimit = Form.useWatch(budgetLimitFieldName, {form});
  const billedLimitPeriod = Form.useWatch(billedFieldName, {form});
  const cardLimit = Form.useWatch(cardLimitFieldName, {form});
  const cardLimitPeriod = Form.useWatch(cardLimitPeriodFieldName, {form});
  const name = Form.useWatch('name', {form});
  const service = Form.useWatch('service', {form});

  const isTooHighLimit = useMemo(() => {
    return cardsHelpers.isTooHighLimit({
      limit: cardLimit,
      maxLimit,
      period: cardLimitPeriod
    });
  }, [cardLimit, cardLimitPeriod, maxLimit]);

  useEffect(() => {
    if (isPersonalCardType && isSelectedHardLimit && budgetLimit !== cardLimit) {
      form.setFieldValue(cardLimitFieldName, budgetLimit);
    }
  }, [budgetLimit]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isPersonalCardType && isSelectedHardLimit) {
      form.setFieldValue(cardLimitPeriodFieldName, getCardLimitTypeFromBilled(billedLimitPeriod));
    }
  }, [billedLimitPeriod]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (open && cardType !== SUBSCRIPTION && !isSelectedHardLimit) handleChangeHardLimit(true);
  }, [cardType, open]); // eslint-disable-line react-hooks/exhaustive-deps

  const vendorProps = useMemo(() => subscriptionsHelpers.getSubscriptionProductProps({isLoadingServices: services.loading, t}), [services, t]);

  const disabledCreateButton = useMemo(() => {
    const cardTypeName = isPersonalCardType ? name : service;
    return cardTypeName === '' || loading || isTooHighLimit
  }, [isPersonalCardType, isTooHighLimit, loading, name, service]);

  const ownerOptions = useMemo(() => formHelpers.getEmployeeOptions({employees, employeeEmail, t}), [employees]); // eslint-disable-line react-hooks/exhaustive-deps

  const trans = (key) => t(`cards:${key}`);

  const formT = (key) => trans(`modal.addNewCard.${key}`);

  const labelT = (key) => formT(`labels.${key}`);

  const placeholderT = (key) => formT(`placeholders.${key}`);

  const subsPlaceholderT = (key) => t(`subscriptions:placeholders.${key}`);

  const tooltipT = (key) => trans(`tooltips.${key}`);

  const footer = (
    <Button
      disabled={disabledCreateButton}
      onClick={() => !disabledCreateButton && form.submit()}
      type='primary'
      size='large'
    >
      {t('create')}
    </Button>
  );

  useEffect(() => {
    form.setFieldValue('owner', employeeEmail);
  }, [employeeEmail]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    form.setFieldValue('card_type', defaultCardType);
    setCardType(defaultCardType);
  }, [defaultCardType]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (open) {
      const limit = getLimit(defaultCardType, paymentCardLimits);
      const formFieldValues = {
        ...defaultFormValues,
        [budgetLimitFieldName]: limit,
        [cardLimitFieldName]: limit,
        owner: employeeEmail
      };
      setInitialFormValues(formFieldValues);
      form.setFieldsValue(formFieldValues);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (cardType === SUBSCRIPTION) {
      setIsOpenDetails({
        ...isOpenDetails,
        subscriptions: true
      });
      setTimeout(() => {
        setIsOpenDetails({
          ...isOpenDetails,
          subscriptions: true,
          user: false
        })
      }, 600);
    } else {
      setIsOpenDetails({
        ...isOpenDetails,
        user: true
      });
      setTimeout(() => {
        setIsOpenDetails({
          ...isOpenDetails,
          subscriptions: false,
          user: true
        })
      }, 500);
    }
  }, [cardType]); // eslint-disable-line react-hooks/exhaustive-deps

  const onChangeTags = (tags) => {
    form.setFieldValue('cost_centers', tags);
    setInitialFormValues({...initialFormValues, cost_centers: tags});
  }

  const handleSubmit = (formData) => {
    const getFieldValue = (key, defaultValue) => objectHelpers.getObjProp(formData, key, defaultValue);
    const {owner, cost_centers: tags} = formData;
    const limitPeriod = getFieldValue(cardLimitPeriodFieldName, defaultCardLimitPeriod);
    let data = {
      [budgetLimitFieldName]: getFieldValue(budgetLimitFieldName, defaultBudgetLimitSubscription),
      [cardLimitFieldName]: getFieldValue(cardLimitFieldName, defaultBudgetLimitSubscription),
      [billedFieldName]: getFieldValue(billedFieldName),
    }
    if (cardType === SUBSCRIPTION) {
      let category = getFieldValue('category');
      data = {
        ...data,
        [cardLimitPeriodFieldName]: limitPeriod,
        card_owner: owner,
        expected_first_payment: dateHelpers.getMomentUnixTimestamp(moment()),
        owner,
        payment_type: subscriptionPaymentTypesConstants.CARD,
        reason: formData.reason,
        service: formData.service,
        status: formData[statusFieldName],
        tags,
      }
      if (category) data = {...data, category};
    } else {
      data = {
        ...data,
        [limitPeriodShortFieldName]: limitPeriod,
        cost_centers: tags,
        owner_email: owner,
        name: formData.name,
      }
    }
    onOk && onOk({
      cardType,
      data,
      errorCallback: (err) => {
        const fields = formHelpers.getFormServerErrorFields(err.response.data);
        form.setFields(fields);
      }
    });
  }

  const handleOnSearchServices = (value) => subscriptionsHelpers.handleSearchSubscriptionService({onSearch: getSuggestServices, services, setServices, value})

  const onClose = (e) => onCancel && onCancel(e);

  const handleChangeHardLimit = (value) => {
    setIsSelectedHardLimit(value);
    if (value) {
      form.setFieldsValue({
        [cardLimitFieldName]: budgetLimit,
        [cardLimitPeriodFieldName]: getCardLimitTypeFromBilled(billedLimitPeriod)
      })
    }
  }

  const budgetFieldRules = subscriptionsHelpers.getBudgetFieldRules(t);

  return (
    <StyledAddNewCardFormModal
      forceRender
      footer={footer}
      onClose={onClose}
      open={open}
      width={448}
      {...rest}
    >
      <SpinSmall spinning={loading}>
        <Form
          initialValues={initialFormValues}
          onFinish={handleSubmit}
          layout='vertical'
          form={form}
          requiredMark={false}
        >

          {cardType === SUBSCRIPTION ? (
            <>
              <Item
                className={service !== '' && 'd-none-label'}
                label={t('product')}
                name='service'
                tooltip={vendorProps.tooltip}
              >
                <SubscriptionSelect
                  autocompleteProps={vendorProps.autocomplete}
                  inputProps={vendorProps.input}
                  minSearchSymbolsLength={minServiceSearchSymbolsLength}
                  onSearch={handleOnSearchServices}
                  services={services.data}
                />
              </Item>
              <Item
                label={t('status')}
                name={statusFieldName}
              >
                <Select
                  options={formHelpers.getStatusOptions(statusOptions)}
                  size='large'
                />
              </Item>
            </>
          ) : (
            <Item
              label={labelT('cardName')}
              name='name'
            >
              <Input
                placeholder={placeholderT('cardName')}
                size='large'
              />
            </Item>
          )}

          <Item
            label={labelT('owner')}
            name='owner'
          >
            <Select
              optionFilterProp='optionLabel'
              options={ownerOptions}
              size='large'
              showSearch
            />
          </Item>
          <Item
            label={labelT('costCenter')}
            name='cost_centers'
            tooltip={{ icon: <InfoCircleOutlined /> }}
          >
            <CompanyTagSelect
              inputProps={{
                placeholder: placeholderT('costCenter'),
                size: 'large'
              }}
              onChange={onChangeTags}
              selected={initialFormValues.cost_centers}
            />
          </Item>
          <StyledBudgetDetailsFormSpace
            align='start'
            size='middle'
          >
            <Item
              label={labelT('budget')}
              name={budgetLimitFieldName}
              rules={budgetFieldRules}
              required
            >
              <EuroInput />
            </Item>
            <Item
              label=' '
              name={billedFieldName}
              rules={requiredRules}
            >
              <Select
                options={billOptions}
                size='large'
              />
            </Item>
          </StyledBudgetDetailsFormSpace>

          {isPersonalCardType && (
            <>
              <Item>
                <HardCardLimitSwitch
                  checked={isSelectedHardLimit}
                  onChange={handleChangeHardLimit}
                  tooltipProps={{
                    title: <>{tooltipT('hardCardLimit1')}<br/>{tooltipT('hardCardLimit2')}</>
                  }}
                />
              </Item>
              <StyledBudgetDetailsFormSpace
                align='start'
                size='middle'
              >
                <Item
                  label={labelT('cardLimit')}
                  name={cardLimitFieldName}
                  rules={budgetFieldRules}
                  required
                >
                  <EuroInput
                    disabled={isSelectedHardLimit}
                  />
                </Item>
                <Item
                  label=' '
                  name={cardLimitPeriodFieldName}
                  rules={requiredRules}
                >
                  <Select
                    disabled={isSelectedHardLimit}
                    options={cardLimitPeriodTypeOptions}
                    size='large'
                  />
                </Item>
              </StyledBudgetDetailsFormSpace>
              <Collapse isOpened={isTooHighLimit}>
                <IncreaseLimitAlert
                  limit={maxLimit}
                />
              </Collapse>
            </>
          )}

          {/* Display card banner only for personal card type*/}
          {isPersonalCardType && <CardBanner />}

          {!isPersonalCardType && (
            <>
              <Divider />
              <Accordion
                items={[
                  {
                    key: '1',
                    label: `${t('advanced')} ${t('settings')}`,
                    children: (
                      <>
                        <StyledBudgetDetailsFormSpace size='middle'>
                          <Item
                            label={`${t('card')} ${t('limit')}`}
                            name={cardLimitFieldName}
                          >
                            <EuroInput />
                          </Item>
                          <Item
                            label=' '
                            name={cardLimitPeriodFieldName}
                            rules={requiredRules}
                          >
                            <Select
                              options={cardLimitPeriodTypeOptions}
                              size='large'
                            />
                          </Item>
                        </StyledBudgetDetailsFormSpace>
                        <Collapse isOpened={isTooHighLimit}>
                          <IncreaseLimitAlert
                            limit={maxLimit}
                            handleBeforeRedirect={onClose}
                          />
                        </Collapse>
                        <Item
                          label={t('category')}
                          name='category'
                        >
                          <Select
                            allowClear={true}
                            options={categoryOptions}
                            placeholder={subsPlaceholderT('category')}
                            size='large'
                          />
                        </Item>
                        <Item
                          label={t('whatIsItFor')}
                          name='reason'
                        >
                          <Input
                            placeholder={subsPlaceholderT('reason')}
                            size='large'
                          />
                        </Item>
                      </>
                    )
                  }
                ]}
              />
            </>
          )}

        </Form>
      </SpinSmall>
    </StyledAddNewCardFormModal>
  );
}

AddNewCardFormModal.propTypes = {
  defaultCardType: PropTypes.any,
  loading: PropTypes.bool,
  onCancel: PropTypes.func,
  onOk: PropTypes.func,
  onSearch: PropTypes.func
}

const mapStateToProps = state => {
  const {employees} = state.company;
  const {employee} = state.user;
  const paymentCardLimits = cardsHelpers.getPaymentCardLimit(state);
  return {
    employees,
    employeeEmail: employee.email,
    paymentCardLimits
  }
}

const mapDispatchToProps = {
  getSuggestServices: serviceStatsActions.getSuggestServices,
}

export default connect(mapStateToProps, mapDispatchToProps)(AddNewCardFormModal);

